import { styled } from '@attentive-platform/stem-ui';

// -----------------------------------------------------------------------------
// ModeLozenge (in index.jsx)
// -----------------------------------------------------------------------------

const modeLozengeBase = styled('span')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  fontSize: '12px',
  fontWeight: 500,
  padding: '2px 10px',
  borderRadius: '999px'
}));

export const ModeLozengeStyles = {
  Neutral: styled(modeLozengeBase)(() => ({
    color: '#4D4D4D',
    background: '#F5F5F5',
    border: '1px solid #EBEBEB'
  })),
  Success: styled(modeLozengeBase)(() => ({
    color: '#0F7A3C',
    background: '#E7F6EE',
    border: '1px solid #C6E9D5'
  })),
  Gold: styled(modeLozengeBase)(() => ({
    color: '#995C00',
    background: '#FFF5E5',
    border: '1px solid #FFEACC'
  }))
};

// -----------------------------------------------------------------------------
// Shared bullet dot for effect lists (used by ConfirmLinkModal + UnlinkModal)
// -----------------------------------------------------------------------------

const bulletRow = styled('div')(() => ({
  display: 'flex',
  gap: '9px',
  alignItems: 'flex-start'
}));

const bulletDot = styled('span')(() => ({
  width: '5px',
  height: '5px',
  borderRadius: '3px',
  background: '#A2B1F6',
  marginTop: '7px',
  flex: 'none'
}));

// -----------------------------------------------------------------------------
// LookupModal
// -----------------------------------------------------------------------------

export const LookupModalStyles = {
  ContentWrap: styled('div')(() => ({
    padding: '10px 20px 20px',
    width: '620px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px'
  })),
  Results: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '9px',
    maxHeight: '360px',
    overflow: 'auto'
  })),
  RowFirst: styled('div')(() => ({
    display: 'grid',
    gridTemplateColumns: 'minmax(0,1fr) 130px 120px',
    gap: '14px',
    alignItems: 'center',
    border: '1px solid #B9C5F8',
    background: '#FBFCFF',
    borderRadius: '10px',
    padding: '13px 15px',
    cursor: 'pointer'
  })),
  RowRest: styled('div')(() => ({
    display: 'grid',
    gridTemplateColumns: 'minmax(0,1fr) 130px 120px',
    gap: '14px',
    alignItems: 'center',
    border: '1px solid #EBEBEB',
    background: '#FFFFFF',
    borderRadius: '10px',
    padding: '13px 15px',
    cursor: 'pointer'
  })),
  Col: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  })),
  ColTrunc: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0
  })),
  CompanyText: styled('span')(() => ({
    fontSize: '14px',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  })),
  EmailText: styled('span')(() => ({
    fontSize: '12.5px',
    color: '#808080',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  })),
  UpperLabel: styled('span')(() => ({
    fontSize: '11.5px',
    color: '#808080',
    letterSpacing: '.03em',
    textTransform: 'uppercase'
  })),
  Actions: styled('div')(() => ({
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px'
  }))
};

// -----------------------------------------------------------------------------
// ConfirmLinkModal
// -----------------------------------------------------------------------------

export const ConfirmLinkModalStyles = {
  ContentWrap: styled('div')(() => ({
    padding: '10px 24px 22px',
    width: '520px',
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  })),
  Heading: styled('span')(() => ({
    fontSize: '19px',
    fontWeight: 600,
    letterSpacing: '-0.005em'
  })),
  FactsBox: styled('div')(() => ({
    border: '1px solid #EBEBEB',
    borderRadius: '10px',
    padding: '14px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '7px'
  })),
  FactRow: styled('div')(() => ({
    display: 'flex',
    justifyContent: 'space-between',
    gap: '14px'
  })),
  Effects: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '7px'
  })),
  BulletRow: bulletRow,
  BulletDot: bulletDot,
  Actions: styled('div')(() => ({
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px'
  }))
};

// -----------------------------------------------------------------------------
// UnlinkModal
// -----------------------------------------------------------------------------

export const UnlinkModalStyles = {
  ContentWrap: styled('div')(() => ({
    padding: '10px 24px 22px',
    width: '520px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px'
  })),
  Heading: styled('span')(() => ({
    fontSize: '19px',
    fontWeight: 600,
    letterSpacing: '-0.005em'
  })),
  Effects: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '7px'
  })),
  BulletRow: bulletRow,
  BulletDot: bulletDot,
  NoteBox: styled('div')(() => ({
    background: '#FAFAFA',
    border: '1px solid #F0F0F0',
    borderRadius: '10px',
    padding: '12px 14px'
  })),
  Actions: styled('div')(() => ({
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px'
  }))
};

// -----------------------------------------------------------------------------
// ScopeLinkage (index.jsx)
// -----------------------------------------------------------------------------

const modeCardBase = styled('div')(() => ({
  borderRadius: '12px',
  padding: '18px',
  display: 'flex',
  flexDirection: 'column',
  gap: '11px'
}));

const modeTagBase = styled('span')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  fontSize: '10.5px',
  fontWeight: 600,
  letterSpacing: '.05em',
  textTransform: 'uppercase',
  background: '#FFFFFF',
  borderRadius: '999px',
  padding: '2px 8px'
}));

const modeStatusChipBase = styled('span')(() => ({
  display: 'block',
  fontSize: '12.5px',
  lineHeight: 1.5,
  borderRadius: '8px',
  padding: '10px 12px',
  textAlign: 'center'
}));

const modeTitleBase = styled('span')(() => ({
  fontSize: '16px',
  fontWeight: 600
}));

export const ScopeLinkageStyles = {
  Wrap: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
    padding: '18px 0'
  })),
  Head: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  })),
  Uppercase: styled('span')(() => ({
    fontSize: '12px',
    color: '#808080',
    letterSpacing: '.03em',
    textTransform: 'uppercase'
  })),
  TitleRow: styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap'
  })),
  H2: styled('h2')(() => ({
    margin: 0,
    fontSize: '22px',
    fontWeight: 600,
    letterSpacing: '-0.01em'
  })),
  ModeCardsGrid: styled('div')(() => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: '14px',
    alignItems: 'stretch'
  })),

  // Mode card variants
  ModeCardIdle: styled(modeCardBase)(() => ({
    border: '1px solid #EBEBEB',
    background: '#FFFFFF'
  })),
  ModeCardCurrent: styled(modeCardBase)(() => ({
    border: '1px solid #B9CFCF',
    background: '#FBFDFD'
  })),
  ModeCardCta: styled(modeCardBase)(() => ({
    border: '1px solid #B9C5F8',
    background: '#FBFCFF'
  })),

  ModeCardHead: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  })),
  ModeTagRow: styled('div')(() => ({
    display: 'inline-flex'
  })),
  ModeTagIdle: styled(modeTagBase)(() => ({
    color: '#4D4D4D',
    border: '1px solid #E0E0E0'
  })),
  ModeTagCurrent: styled(modeTagBase)(() => ({
    color: '#0D3B3E',
    border: '1px solid #B9CFCF'
  })),
  ModeTagCta: styled(modeTagBase)(() => ({
    color: '#112FBB',
    border: '1px solid #B9C5F8'
  })),
  ModeTitleIdle: styled(modeTitleBase)(() => ({
    color: '#1A1A1A'
  })),
  ModeTitleCta: styled(modeTitleBase)(() => ({
    color: '#112FBB'
  })),
  ModeCta: styled('div')(() => ({
    marginTop: 'auto',
    paddingTop: '4px'
  })),
  ModeStatusStack: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  })),
  ModeStatusChipCurrent: styled(modeStatusChipBase)(() => ({
    color: '#3C5A5A',
    background: '#F4F7F7',
    border: '1px solid #B9CFCF'
  })),
  ModeStatusChipIdle: styled(modeStatusChipBase)(() => ({
    color: '#808080',
    background: '#FCFCFD',
    border: '1px solid #F0F0F0'
  })),

  RecordCard: styled('div')(() => ({
    border: '1px solid #EBEBEB',
    borderRadius: '12px',
    overflow: 'hidden'
  })),
  RecordHeader: styled('div')(() => ({
    padding: '15px 20px',
    background: '#FAFAFA',
    borderBottom: '1px solid #EBEBEB',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    flexWrap: 'wrap'
  })),
  RecordHeaderLeft: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '3px'
  })),
  RecordHeaderActions: styled('div')(() => ({
    display: 'flex',
    gap: '10px',
    alignItems: 'center'
  })),
  ConsoleLink: styled('a')(() => ({
    fontSize: '13px',
    color: '#4361EE',
    textDecoration: 'none'
  })),
  RecordBody: styled('div')(() => ({
    padding: '18px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  })),
  MetaRow: styled('div')(() => ({
    display: 'flex',
    gap: '30px',
    flexWrap: 'wrap'
  })),
  MetaCol: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '3px'
  })),
  UpperLabel: styled('span')(() => ({
    fontSize: '11.5px',
    color: '#808080',
    letterSpacing: '.03em',
    textTransform: 'uppercase'
  })),
  TradesBlock: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '9px',
    borderTop: '1px solid #F5F5F5',
    paddingTop: '14px'
  })),
  TradeChips: styled('div')(() => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px'
  })),
  TradeChip: styled('span')(() => ({
    fontSize: '12.5px',
    color: '#4D4D4D',
    background: '#FAFAFA',
    border: '1px solid #EBEBEB',
    borderRadius: '6px',
    padding: '5px 11px',
    whiteSpace: 'nowrap'
  })),
  GateOk: styled('div')(() => ({
    display: 'flex',
    gap: '10px',
    background: '#F4F9F5',
    border: '1px solid #CBE3CD',
    borderRadius: '10px',
    padding: '13px 15px'
  })),
  GateBlocked: styled('div')(() => ({
    display: 'flex',
    gap: '10px',
    background: '#FFFBF5',
    border: '1px solid #FFEACC',
    borderRadius: '10px',
    padding: '13px 15px'
  })),
  GateOkIcon: styled('span')(() => ({
    color: '#3E8E41',
    fontSize: '13px',
    lineHeight: 1.5
  })),
  GateBlockedIcon: styled('span')(() => ({
    color: '#995C00',
    fontSize: '13px',
    lineHeight: 1.5
  })),
  ActivityCard: styled('div')(() => ({
    border: '1px solid #EBEBEB',
    borderRadius: '12px',
    padding: '18px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  })),
  ActivityHeader: styled('div')(() => ({
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: '14px',
    flexWrap: 'wrap'
  })),
  ActivityRow: styled('div')(() => ({
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
    borderTop: '1px solid #F5F5F5',
    paddingTop: '10px'
  })),
  ActivityDot: styled('span')(() => ({
    width: '6px',
    height: '6px',
    borderRadius: '3px',
    background: '#B9C5F8',
    marginTop: '7px',
    flex: 'none'
  })),
  ActivityText: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0
  }))
};
